import React from "react"
import { useTranslation } from "react-i18next"

import { useConnectivityRecovery } from "@/contexts/connectivity-recovery-context"

import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined"
import { Button, Stack, Typography } from "@mui/material"

interface ConnectivityErrorScreenProps {
  isRetrying?: boolean
  onRetry: () => void
}

export const ConnectivityErrorScreen: React.FC<
  ConnectivityErrorScreenProps
> = ({ isRetrying = false, onRetry }) => {
  const { t } = useTranslation("common")
  const { registerRecoverySurface } = useConnectivityRecovery()

  // Register before paint so isRecoveryActive is true on the first frame.
  React.useLayoutEffect(
    () => registerRecoverySurface(),
    [registerRecoverySurface]
  )

  return (
    <Stack
      direction="column"
      role="alert"
      spacing={1}
      sx={{
        alignItems: "center",
        bottom: 0,
        justifyContent: "center",
        left: 0,
        position: "absolute",
        px: 2,
        right: 0,
        top: 0,
      }}
    >
      <CloudOffOutlinedIcon
        aria-hidden="true"
        data-testid="connectivity-error-illustration"
        sx={{ color: "neutral.main", fontSize: 120 }}
      />
      <Typography align="center" component="h1" variant="h1">
        {t("connectivity.title")}
      </Typography>
      <Typography
        align="center"
        sx={{ color: "text.secondary", pb: 2 }}
        variant="body1"
      >
        {t("connectivity.description")}
      </Typography>
      <Button
        disabled={isRetrying}
        loading={isRetrying}
        onClick={() => {
          onRetry()
        }}
        variant="contained"
      >
        {t("button.retry")}
      </Button>
    </Stack>
  )
}
